import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import { useMediaQuery } from '@mui/material';
import React, { createContext, useContext, useState, useMemo } from 'react';

const ThemeContext = createContext({
  mode: 'light',
  toggleTheme: () => {},
});

const getColorPalette = (mode) => {
  if (mode === 'dark') {
    // Vibrant lighter blue color scheme for dark mode
    return {
      primary: {
        main: '#67B7FF',        // Bright sky blue
        light: '#A0D2FF',       // Very light sky blue
        dark: '#3B9EFF',        // Vivid blue
      },
      secondary: {
        main: '#8B96FF',        // Bright indigo
        light: '#B8C1FF',       // Light periwinkle
        dark: '#6B7DFF',        // Vivid indigo
      },
      success: {
        main: '#3DD68C',        // Bright emerald
        light: '#6EE7B7',       // Light emerald
        dark: '#10B981',        // Dark emerald
      },
    };
  } else {
    // Tan/beige color scheme for light mode
    return {
      primary: {
        main: '#D4A373',        // Warm tan
        light: '#E5C8A6',       // Light tan
        dark: '#B88A5F',        // Deep tan
      },
      secondary: {
        main: '#C8956E',        // Terracotta
        light: '#DDB392',       // Light terracotta
        dark: '#A67B5B',        // Deep terracotta
      },
      success: {
        main: '#8B7355',        // Warm brown
        light: '#A89079',       // Light brown
        dark: '#6D5C47',        // Deep brown
      },
    };
  }
};

const baseTheme = {
  palette: {},
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      '@media (max-width:600px)': {
        fontSize: '2rem',
      },
    },
    h2: {
      fontSize: '2rem',
      '@media (max-width:600px)': {
        fontSize: '1.75rem',
      },
    },
    h3: {
      fontSize: '1.75rem',
      '@media (max-width:600px)': {
        fontSize: '1.5rem',
      },
    },
    body1: {
      fontSize: '1rem',
      '@media (max-width:600px)': {
        fontSize: '0.875rem',
      },
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          padding: '8px 16px',
          '@media (max-width:600px)': {
            width: '100%',
            marginBottom: '8px',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          '@media (max-width:600px)': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '0 !important',
          paddingRight: '0 !important',
          padding: '0 !important',
          margin: '0 !important',
          maxWidth: '100% !important',
          '@media (min-width: 600px)': {
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
          },
          '@media (max-width:600px)': {
            padding: '0 !important',
          },
        },
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
};

export function ThemeProvider({ children }) {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('themeMode');
    return savedMode || (prefersDarkMode ? 'dark' : 'light');
  });

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      localStorage.setItem('themeMode', newMode);
      return newMode;
    });
  };

  const theme = useMemo(
    () => {
      const colorPalette = getColorPalette(mode);
      return createTheme({
        ...baseTheme,
        palette: {
          ...baseTheme.palette,
          ...colorPalette,
          mode,
          background: {
            default: mode === 'dark' ? '#0D1117' : '#FAF8F5',
            paper: mode === 'dark' ? '#161B22' : '#FFFFFF',
          },
          text: {
            primary: mode === 'dark' ? '#F0F4F8' : '#2D1B0E',
            secondary: mode === 'dark' ? '#8B96B8' : '#8B7355',
          },
        },
      });
    },
    [mode]
  );

  const value = useMemo(() => ({ mode, toggleTheme }), [mode]);

  return (
    <ThemeContext.Provider value={value}>
      <StyledEngineProvider injectFirst>
        <MuiThemeProvider theme={theme}>
          <StylesThemeProvider theme={theme}>
            {children}
          </StylesThemeProvider>
        </MuiThemeProvider>
      </StyledEngineProvider>
    </ThemeContext.Provider>
  );
}

export function useDarkMode() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a ThemeProvider');
  }
  return {
    value: context.mode === 'dark',
    toggle: context.toggleTheme,
  };
}